<script setup>
import { computed, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PriorityFlagIcon from 'dashboard/components-next/icon/PriorityFlagIcon.vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  selectedItem: {
    type: Object,
    default: () => ({}),
  },
  placeholder: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['select']);
const isOpen = ref(false);

const currentItem = computed(
  () => props.selectedItem || props.options[0] || {}
);

const isSelected = option => option.id === currentItem.value?.id;

const selectOption = option => {
  emit('select', option);
  isOpen.value = false;
};
</script>

<template>
  <OnClickOutside @trigger="isOpen = false">
    <div class="relative mb-2 w-full" @keyup.esc="isOpen = false">
      <button
        type="button"
        class="group flex h-10 w-full items-center gap-2.5 rounded-xl border border-n-weak bg-n-solid-1 px-2.5 text-left shadow-sm transition hover:border-n-strong hover:bg-n-alpha-1 focus-visible:border-n-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/20"
        :aria-expanded="isOpen"
        aria-haspopup="listbox"
        @click="isOpen = !isOpen"
      >
        <PriorityFlagIcon
          :priority="currentItem.id || ''"
          class="size-5 shrink-0"
        />
        <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
          {{ currentItem.name || placeholder }}
        </span>
        <Icon
          icon="i-lucide-chevron-down"
          class="size-4 shrink-0 text-n-slate-9 transition-transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </button>

      <div
        v-if="isOpen"
        class="absolute inset-x-0 top-[calc(100%+0.375rem)] z-[9999] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 p-1.5 shadow-xl"
      >
        <p
          class="mb-1 px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-n-slate-9"
        >
          {{ title }}
        </p>
        <ul class="m-0 grid list-none gap-1 p-0" role="listbox">
          <li v-for="option in options" :key="String(option.id)">
            <button
              type="button"
              role="option"
              class="flex h-10 w-full items-center gap-2.5 rounded-xl px-2.5 text-left transition"
              :class="
                isSelected(option)
                  ? 'bg-n-brand/10 text-n-slate-12 ring-1 ring-inset ring-n-brand/15'
                  : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
              "
              :aria-selected="isSelected(option)"
              @click="selectOption(option)"
            >
              <PriorityFlagIcon
                :priority="option.id || ''"
                class="size-5 shrink-0"
              />
              <span class="min-w-0 flex-1 truncate text-sm font-medium">
                {{ option.name }}
              </span>
              <Icon
                v-if="isSelected(option)"
                icon="i-lucide-check"
                class="size-4 shrink-0 text-n-brand"
              />
            </button>
          </li>
        </ul>
      </div>
    </div>
  </OnClickOutside>
</template>
